import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { toast } from 'react-toastify';
import { API } from '../../../api';
import ConfirmDialog from '../../DialogUi/ConfirmDialog';
import ImproveDialog from '../ImproveDialog';
import RuntimeMeter from '../RuntimeMeter';
import { budgetFor, countWords, formatApprox, isEditAllowed, itemSeconds } from '../duration';
import { fieldSx, pillButtonSx, vb } from '../theme';
import { PresenterSection, VideoProject } from '../types';
import { apiCode, apiMessage, useAutosave } from '../useAutosave';

interface LocalSection {
  id: string;
  title: string;
  narration: string;
  actualSeconds: number | null;
}

const fromProject = (p: VideoProject): LocalSection[] =>
  p.sections.map((s) => ({
    id: s.id,
    title: s.title,
    narration: s.narration,
    actualSeconds: s.audio?.durationSeconds ?? null
  }));

// Presenter step 2 (§10–§13): editable speech sections with the GLOBAL
// duration budget enforced on every change, autosave, per-section AI improve.
const ScriptEditor: React.FC<{
  project: VideoProject;
  lessonName: string;
  onProject: (p: VideoProject) => void;
  onPreview: () => void;
  onBackToGuidance: () => void;
}> = ({ project, lessonName, onProject, onPreview, onBackToGuidance }) => {
  const [sections, setSections] = useState<LocalSection[]>(() => fromProject(project));
  const [limitHit, setLimitHit] = useState<string | null>(null);
  const [improveId, setImproveId] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const serverRef = useRef(project);
  useEffect(() => {
    if (project.contentVersion !== serverRef.current.contentVersion || project.id !== serverRef.current.id) {
      serverRef.current = project;
      setSections(fromProject(project));
    }
  }, [project]);

  const budget = budgetFor('presenter', project.targetDurationSeconds);
  const total = useMemo(
    () => Math.round(sections.reduce((s, x) => s + itemSeconds(x.narration, x.actualSeconds), 0) * 10) / 10,
    [sections]
  );
  const words = useMemo(() => sections.reduce((s, x) => s + countWords(x.narration), 0), [sections]);

  const save = useCallback(
    async (value: LocalSection[]) => {
      const res: any = await API.putVideoProjectSections({
        projectId: project.id,
        sections: value.map((s) => ({ id: s.id, title: s.title, narration: s.narration }))
      });
      serverRef.current = res.data;
      onProject(res.data as VideoProject);
    },
    [project.id, onProject]
  );
  const reject = useCallback((err: unknown) => {
    const code = apiCode(err);
    setSections(fromProject(serverRef.current));
    if (code === 'DURATION_BUDGET') setLimitHit('Video length reached. Shorten another section to add more narration.');
    else toast.error(apiMessage(err, 'Could not save your changes'));
  }, []);
  const { schedule, flush, saving, dirty } = useAutosave(save, reject);

  const apply = (next: LocalSection[]) => {
    const nextTotal = next.reduce((s, x) => s + itemSeconds(x.narration, x.actualSeconds), 0);
    if (!isEditAllowed(total, nextTotal, budget)) {
      setLimitHit('Video length reached. Shorten another section to add more narration.');
      return;
    }
    setLimitHit(null);
    setSections(next);
    schedule(next);
  };

  const improve = async (instruction: string | null) => {
    if (!improveId) return;
    try {
      setBusy(improveId);
      await flush();
      const res: any = await API.improvePresenterSection({
        projectId: project.id,
        sectionId: improveId,
        instruction
      });
      serverRef.current = res.data;
      onProject(res.data as VideoProject);
      setSections(fromProject(res.data));
      setImproveId(null);
    } catch (err) {
      const code = apiCode(err);
      toast.error(
        code === 'DURATION_BUDGET'
          ? 'The improved section would exceed the video length. Shorten another section first.'
          : apiMessage(err, 'Elliott could not improve this section. Try again.')
      );
    } finally {
      setBusy(null);
    }
  };

  const [confirmRewrite, setConfirmRewrite] = useState(false);
  const rewrite = async () => {
    setConfirmRewrite(false);
    try {
      setBusy('rewrite');
      const res: any = await API.draftPresenterScript({ projectId: project.id });
      serverRef.current = res.data;
      onProject(res.data as VideoProject);
      setSections(fromProject(res.data));
    } catch (err) {
      toast.error(apiMessage(err, 'Elliott could not rewrite the script. Try again.'));
    } finally {
      setBusy(null);
    }
  };

  const preview = async () => {
    if (sections.some((s) => !s.narration.trim())) {
      toast.error('Every section needs narration before a preview.');
      return;
    }
    await flush();
    onPreview();
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minHeight: 0 }} data-testid="script-editor">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 240 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontFamily: vb.font, fontSize: 18, fontWeight: 800, color: vb.text }}>
              Script · {lessonName}
            </Typography>
            <Box
              sx={{
                fontFamily: vb.font,
                fontSize: 10,
                fontWeight: 800,
                letterSpacing: '0.1em',
                px: 0.7,
                py: 0.25,
                borderRadius: '4px',
                background: vb.border,
                color: vb.textSecondary
              }}
            >
              PRESENTER
            </Box>
          </Box>
          <Typography sx={{ fontFamily: vb.font, fontSize: 12.5, color: vb.textTertiary }}>
            Elliott reads exactly what&apos;s here · live captions follow the script
          </Typography>
        </Box>
        <Button
          onClick={() => setConfirmRewrite(true)}
          disabled={busy !== null}
          sx={pillButtonSx()}
          data-testid="script-rewrite"
        >
          {busy === 'rewrite' ? (
            'Rewriting...'
          ) : (
            <>
              Rewrite Script{' '}
              <Box component="span" sx={{ color: vb.textTertiary, ml: 0.5 }}>
                · free
              </Box>
            </>
          )}
        </Button>
      </Box>

      <Box
        sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, overflowY: 'auto', pr: 0.5, flex: 1, minHeight: 0 }}
      >
        {sections.map((s, i) => (
          <Box
            key={s.id}
            data-testid={`section-${i}`}
            sx={{ borderRadius: '12px', border: `1px solid ${vb.border}`, background: vb.page, p: 2 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1 }}>
              <Typography
                sx={{ fontFamily: vb.font, fontSize: 11, fontWeight: 800, letterSpacing: '0.1em', color: vb.brand }}
              >
                SECTION {String(i + 1).padStart(2, '0')}
              </Typography>
              <TextField
                size="small"
                value={s.title}
                onChange={(e) => apply(sections.map((x) => (x.id === s.id ? { ...x, title: e.target.value } : x)))}
                inputProps={{
                  'aria-label': `Section ${i + 1} title`,
                  maxLength: 120,
                  'data-testid': `section-${i}-title`
                }}
                sx={{
                  ...fieldSx,
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    ...fieldSx['& .MuiOutlinedInput-root'],
                    fontWeight: 700,
                    background: 'transparent'
                  }
                }}
              />
              <Typography
                sx={{ fontFamily: vb.font, fontSize: 12, color: vb.textTertiary, whiteSpace: 'nowrap' }}
                data-testid={`section-${i}-seconds`}
              >
                {formatApprox(itemSeconds(s.narration, s.actualSeconds))}
              </Typography>
              <Tooltip title="Delete section">
                <span>
                  <IconButton
                    size="small"
                    aria-label={`Delete section ${i + 1}`}
                    disabled={sections.length <= 1}
                    onClick={() => apply(sections.filter((x) => x.id !== s.id))}
                    sx={{ color: vb.textTertiary }}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
            <TextField
              multiline
              minRows={3}
              fullWidth
              value={s.narration}
              onChange={(e) =>
                apply(
                  sections.map((x) => (x.id === s.id ? { ...x, narration: e.target.value, actualSeconds: null } : x))
                )
              }
              inputProps={{ 'aria-label': `Section ${i + 1} narration`, 'data-testid': `section-${i}-narration` }}
              sx={fieldSx}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
              <Button
                size="small"
                onClick={() => setImproveId(s.id)}
                disabled={busy !== null}
                sx={{ ...pillButtonSx(), py: 0.5, fontSize: 12 }}
                data-testid={`section-${i}-improve`}
              >
                Improve with Elliott
              </Button>
            </Box>
          </Box>
        ))}
      </Box>

      {limitHit && (
        <Typography
          role="alert"
          sx={{ fontFamily: vb.font, fontSize: 12.5, color: vb.danger }}
          data-testid="limit-hint"
        >
          {limitHit}
        </Typography>
      )}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
          pt: 1.5,
          borderTop: `1px solid ${vb.border}`
        }}
      >
        <RuntimeMeter
          label="Estimated runtime"
          narrationSeconds={total}
          lockedSeconds={0}
          targetSeconds={project.targetDurationSeconds}
          budgetSeconds={budget}
          extra={`${words} words`}
        />
        <Box sx={{ flex: 1 }} />
        <Typography sx={{ fontFamily: vb.font, fontSize: 12, color: vb.textTertiary }}>
          {saving ? 'Saving...' : dirty ? 'Unsaved' : 'Nothing is saved until you save the video'}
        </Typography>
        <Button onClick={onBackToGuidance} sx={pillButtonSx()}>
          Guidance
        </Button>
        <Button
          data-testid="preview-video"
          onClick={preview}
          disabled={busy !== null || total > budget + 0.05}
          sx={pillButtonSx(true)}
        >
          Preview Video
        </Button>
      </Box>

      <ConfirmDialog
        open={confirmRewrite}
        title="Rewrite the whole script?"
        message="Your edits will be replaced by a fresh draft. This is free."
        confirmLabel="Rewrite"
        onConfirm={rewrite}
        onClose={() => setConfirmRewrite(false)}
      />
      <ImproveDialog
        open={improveId !== null}
        title="Improve this section"
        busy={busy === improveId && improveId !== null}
        onClose={() => setImproveId(null)}
        onSubmit={improve}
      />
    </Box>
  );
};

export default ScriptEditor;
